import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DoctorCrmService } from 'app/services/doctor-crm.service';
import { DoctorCrmModel } from 'app/models/doctor-crm.model';
import { CFMService } from 'app/shared/services/cfm.service';
import { DATETIME_PICKER_IMPORTS } from 'app/shared/datetime-picker/datetime-picker.imports';
import { finalize, take, debounceTime, distinctUntilChanged, filter } from 'rxjs';
import { AutoTranslatePipe } from 'app/shared/pipes/translate.pipe';
import { AutoTranslateDirective } from 'app/shared/directives/auto-translate.directive';
import { NgxMaskDirective } from 'ngx-mask';
import { DevstroupeConfirmationService } from '@devstroupe/services/confirmation';
import { RecordIdBadgeComponent } from 'app/shared/components/record-id-badge/record-id-badge.component';

@Component({
    selector: 'app-doctor-crm-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        ...DATETIME_PICKER_IMPORTS,
        AutoTranslatePipe,
        AutoTranslateDirective,
        NgxMaskDirective,
        RecordIdBadgeComponent,
    ],
    templateUrl: './doctor-crm-dialog.component.html',
})
export class DoctorCrmDialogComponent implements OnInit {
    form: FormGroup;
    loading = false;
    removing = false;
    searchingCrm = false;
    crmValidated = false;

    constructor(
        private fb: FormBuilder,
        public dialogRef: MatDialogRef<DoctorCrmDialogComponent>,
        private doctorCrmService: DoctorCrmService,
        private cfmService: CFMService,
        private _devstroupeConfirmationService: DevstroupeConfirmationService,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            doctor_id: number;
            doctor_crm?: DoctorCrmModel; // Se fornecido, está editando
        },
    ) {
        const isEdit = !!data.doctor_crm;

        this.form = this.fb.group({
            crm_number: [
                isEdit ? data.doctor_crm?.crm_number : '',
                [Validators.required, Validators.pattern(/^\d+$/)],
            ],
            uf: [isEdit ? data.doctor_crm?.uf : '', [Validators.required]],
            status: [isEdit ? data.doctor_crm?.status : null],
            specialties: [isEdit ? data.doctor_crm?.specialties : null],
            license_issue_date: [isEdit ? data.doctor_crm?.license_issue_date : null],
            license_expiry_date: [isEdit ? data.doctor_crm?.license_expiry_date : null],
            license_issue_date_emission: [isEdit ? data.doctor_crm?.license_issue_date_emission : null],
        });

        // Adicionar máscara para campo combinado professional_number (usado no template)
        this.form.addControl('professional_number', this.fb.control(isEdit ? `${data.doctor_crm?.crm_number}-${data.doctor_crm?.uf}` : '', [
            Validators.required,
            Validators.pattern(/^\d+-[A-Z]{2}$/),
        ]));
    }

    ngOnInit(): void {
        // Listener para mudanças no professional_number
        this.form.controls['professional_number'].valueChanges
            .pipe(
                debounceTime(800),
                distinctUntilChanged(),
                filter((value) => {
                    return value && /^\d+-[A-Z]{2}$/.test(value);
                }),
            )
            .subscribe(() => {
                this.consultMedicAutomatically();
            });
    }

    consultMedicAutomatically(): void {
        const professionalNumber = this.form.controls['professional_number'].value;

        if (!professionalNumber || this.searchingCrm || this.crmValidated) {
            return;
        }

        const crmRegex = /^(\d+)-([A-Z]{2})$/;
        const match = professionalNumber.match(crmRegex);

        if (!match) {
            return;
        }

        const [, crm, uf] = match;

        this.searchingCrm = true;
        const data = {
            crm: parseInt(crm, 10),
            uf: uf,
            cpf: null,
        };

        this.cfmService.consult(data).subscribe({
            next: (res) => {
                this.form.controls['professional_number'].setValue(`${res.crm}-${res.uf}`, { emitEvent: false });
                this.form.controls['crm_number'].setValue(res.crm.toString(), { emitEvent: false });
                this.form.controls['uf'].setValue(res.uf, { emitEvent: false });
                this.form.controls['status'].setValue(res.situacao, { emitEvent: false });
                this.form.controls['specialties'].setValue(res.especialidade, { emitEvent: false });
                this.crmValidated = true;
                this.searchingCrm = false;
            },
            error: (error) => {
                this.searchingCrm = false;
                console.warn('Erro ao consultar CFM automaticamente:', error);
            },
        });
    }

    consultMedic(): void {
        const professionalNumber = this.form.controls['professional_number'].value;

        const crmRegex = /^(\d+)-([A-Z]{2})$/;
        const match = professionalNumber?.match(crmRegex);

        if (!match) {
            alert('CRM deve estar no formato: NÚMERO-UF (ex: 12345-GO)');
            return;
        }

        const [, crm, uf] = match;

        const data = {
            crm: parseInt(crm, 10),
            uf: uf,
            cpf: null,
        };

        this.searchingCrm = true;
        this.cfmService.consult(data).subscribe({
            next: (res) => {
                this.form.controls['professional_number'].setValue(`${res.crm}-${res.uf}`, { emitEvent: false });
                this.form.controls['crm_number'].setValue(res.crm.toString(), { emitEvent: false });
                this.form.controls['uf'].setValue(res.uf, { emitEvent: false });
                this.form.controls['status'].setValue(res.situacao, { emitEvent: false });
                this.form.controls['specialties'].setValue(res.especialidade, { emitEvent: false });
                this.crmValidated = true;
                this.searchingCrm = false;
            },
            error: (error) => {
                this.searchingCrm = false;
                alert(`Erro ao consultar CFM: ${error.error?.message || error.message || 'Erro desconhecido'}`);
            },
        });
    }

    onCrmBlur(): void {
        const value = this.form.controls['professional_number'].value;
        if (value && typeof value === 'string') {
            const cleaned = value.trim().toUpperCase();
            if (!cleaned.includes('-')) {
                // Se não tiver hífen, tentar adicionar baseado no UF do form
                const uf = this.form.controls['uf'].value;
                if (uf) {
                    this.form.controls['professional_number'].setValue(`${cleaned}-${uf.toUpperCase()}`);
                }
            } else {
                this.form.controls['professional_number'].setValue(cleaned);
            }
        }
    }

    save(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        this.loading = true;

        const formValue = this.form.getRawValue();
        const professionalNumber = formValue.professional_number;
        const crmRegex = /^(\d+)-([A-Z]{2})$/;
        const match = professionalNumber.match(crmRegex);

        if (!match) {
            alert('CRM deve estar no formato: NÚMERO-UF (ex: 12345-GO)');
            this.loading = false;
            return;
        }

        const [, crmNumber, uf] = match;

        const crmData = {
            crm_number: crmNumber,
            uf: uf,
            status: formValue.status,
            specialties: formValue.specialties,
            license_issue_date: formValue.license_issue_date,
            license_expiry_date: formValue.license_expiry_date,
            license_issue_date_emission: formValue.license_issue_date_emission,
        };

        if (this.data.doctor_crm?.doctor_crm_id) {
            // Editar CRM existente
            this.doctorCrmService
                .updateCrm(this.data.doctor_crm.doctor_crm_id, crmData)
                .pipe(
                    take(1),
                    finalize(() => (this.loading = false)),
                )
                .subscribe({
                    next: () => {
                        this.dialogRef.close(true);
                    },
                    error: (error) => {
                        console.error('Erro ao atualizar CRM:', error);
                        alert(`Erro ao atualizar CRM: ${error.error?.message || error.message || 'Erro desconhecido'}`);
                    },
                });
        } else {
            // Criar novo CRM
            this.doctorCrmService
                .createCrm(this.data.doctor_id, { crm_number: crmNumber, uf: uf })
                .pipe(
                    take(1),
                    finalize(() => (this.loading = false)),
                )
                .subscribe({
                    next: (createdCrm) => {
                        // Backend já consultou CFM e retornou status/especialidades corretos
                        // Atualizar o formulário com os valores retornados pelo backend
                        // Isso garante que o usuário veja o status correto imediatamente
                        if (createdCrm.status) {
                            this.form.controls['status'].setValue(createdCrm.status, { emitEvent: false });
                        }
                        if (createdCrm.specialties) {
                            this.form.controls['specialties'].setValue(createdCrm.specialties, { emitEvent: false });
                        }
                        
                        // Apenas atualizar campos adicionais se:
                        // 1. Campos de data foram preenchidos, OU
                        // 2. CFM foi consultado manualmente (crmValidated = true) e há valores para status/specialties
                        
                        const hasDates = crmData.license_issue_date || crmData.license_expiry_date || crmData.license_issue_date_emission;
                        const hasCfmData = this.crmValidated && (crmData.status || crmData.specialties);
                        
                        if (hasDates || hasCfmData) {
                            // Preparar dados para update
                            const updateData: any = {};
                            
                            // Apenas atualizar status/specialties se CFM foi consultado
                            if (this.crmValidated) {
                                if (crmData.status) {
                                    updateData.status = crmData.status;
                                }
                                if (crmData.specialties) {
                                    updateData.specialties = crmData.specialties;
                                }
                            }
                            
                            // Sempre atualizar datas se fornecidas
                            if (crmData.license_issue_date) {
                                updateData.license_issue_date = crmData.license_issue_date;
                            }
                            if (crmData.license_expiry_date) {
                                updateData.license_expiry_date = crmData.license_expiry_date;
                            }
                            if (crmData.license_issue_date_emission) {
                                updateData.license_issue_date_emission = crmData.license_issue_date_emission;
                            }
                            
                            this.doctorCrmService
                                .updateCrm(createdCrm.doctor_crm_id, updateData)
                                .pipe(take(1))
                                .subscribe({
                                    next: () => {
                                        this.dialogRef.close(true);
                                    },
                                    error: (error) => {
                                        console.error('Erro ao atualizar campos adicionais do CRM:', error);
                                        // Ainda fechar o dialog pois o CRM foi criado com status correto do backend
                                        this.dialogRef.close(true);
                                    },
                                });
                        } else {
                            // Não há campos adicionais para atualizar
                            // O backend já retornou status/especialidades corretos
                            this.dialogRef.close(true);
                        }
                    },
                    error: (error) => {
                        console.error('Erro ao criar CRM:', error);
                        alert(`Erro ao criar CRM: ${error.error?.message || error.message || 'Erro desconhecido'}`);
                    },
                });
        }
    }

    cancel(): void {
        this.dialogRef.close(false);
    }

    remove(): void {
        const crm = this.data.doctor_crm;
        if (!crm?.doctor_crm_id) {
            return;
        }

        this._devstroupeConfirmationService
            .open({
                title: 'Deletar CRM',
                message: `Deseja realmente deletar o CRM ${crm.registration}?`,
                actions: {
                    confirm: { show: true, label: 'Deletar', color: 'warn' },
                    cancel: { show: true, label: 'Cancelar' },
                },
            })
            .afterClosed()
            .subscribe((result: boolean) => {
                if (!result) {
                    return;
                }
                this.removing = true;
                this.doctorCrmService
                    .deleteCrm(crm.doctor_crm_id)
                    .pipe(
                        take(1),
                        finalize(() => (this.removing = false)),
                    )
                    .subscribe({
                        next: () => {
                            this.dialogRef.close(true);
                        },
                        error: (error) => {
                            console.error('Erro ao deletar CRM:', error);
                            alert(`Erro ao deletar CRM: ${error.error?.message || error.message || 'Erro desconhecido'}`);
                        },
                    });
            });
    }
}
